import React, { useEffect, useMemo, useRef } from 'react';
// chartist 0.11 ships no type declarations; legacy DateLineChart.jsx uses it the same way.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import Chartist from 'chartist';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import 'chartist/dist/chartist.min.css';
import { useTranslator } from '@front-activerse/common-components';
import type { MetricChartPoint } from '../types';

interface Props {
  points: MetricChartPoint[];
  ariaLabel: string;
  unit?: string;
}

const formatXLabel = (value: unknown): string => {
  if (typeof value !== 'string') return String(value ?? '');
  const parts = value.split('-');
  if (parts.length === 3) return `${parts[2]}.${parts[1]}`;
  return value;
};

const formatTooltipDate = (raw: string): string => {
  const parts = raw.split('-');
  if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
  return raw;
};

const buildNumberFormatter = (locale: string) => (n: number) => {
  if (Number.isInteger(n)) return n.toLocaleString(locale || undefined);
  return n.toLocaleString(locale || undefined, { maximumFractionDigits: 2 });
};

const MetricChart: React.FC<Props> = ({ points, ariaLabel, unit }) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const { language } = useTranslator();
  const formatNumber = useMemo(() => buildNumberFormatter(language), [language]);

  useEffect(() => {
    if (!chartRef.current || !wrapperRef.current || !tooltipRef.current) return undefined;

    const labels = points.map((p) => p.date);
    const series = [points.map((p) => (p.value === null ? null : p.value))];

    const tooltip = tooltipRef.current;
    const wrapper = wrapperRef.current;

    // Show roughly 7 evenly-spaced X labels regardless of how many points we
    // render — for "last month" / "last 3 months" the raw labels overlap.
    const targetLabelCount = 7;
    const step = Math.max(1, Math.ceil(labels.length / targetLabelCount));
    let xLabelIndex = -1;
    const interpolateXLabel = (value: unknown): string => {
      xLabelIndex += 1;
      if (xLabelIndex % step !== 0 && xLabelIndex !== labels.length - 1) return '';
      return formatXLabel(value);
    };

    const chart = new Chartist.Line(
      chartRef.current,
      { labels, series },
      {
        fullWidth: true,
        showArea: false,
        lineSmooth: Chartist.Interpolation.none({ fillHoles: false }),
        axisX: {
          labelInterpolationFnc: interpolateXLabel,
        },
        axisY: {
          onlyInteger: true,
          offset: 56,
          labelInterpolationFnc: (label: unknown) => formatNumber(Number(label)),
        },
        chartPadding: { top: 16, right: 24, bottom: 8, left: 8 },
      }
    );

    const handlers: Array<{ el: SVGElement; enter: () => void; leave: () => void }> = [];

    chart.on('draw', (data: { type: string; element: { _node: SVGElement }; index: number }) => {
      if (data.type !== 'point') return;
      const node = data.element._node;
      const point = points[data.index];
      if (!point || point.value === null) return;

      const enter = () => {
        const rect = node.getBoundingClientRect();
        const wrapperRect = wrapper.getBoundingClientRect();
        const left = rect.left - wrapperRect.left + rect.width / 2;
        const top = rect.top - wrapperRect.top;
        tooltip.style.left = `${left}px`;
        tooltip.style.top = `${top}px`;
        tooltip.textContent = `${formatTooltipDate(point.date)}: ${formatNumber(
          point.value as number
        )}${unit ? ` ${unit}` : ''}`;
        tooltip.style.opacity = '1';
      };
      const leave = () => {
        tooltip.style.opacity = '0';
      };

      node.addEventListener('mouseenter', enter);
      node.addEventListener('mouseleave', leave);
      node.addEventListener('touchstart', enter);
      node.addEventListener('touchend', leave);
      handlers.push({ el: node, enter, leave });
    });

    return () => {
      handlers.forEach(({ el, enter, leave }) => {
        el.removeEventListener('mouseenter', enter);
        el.removeEventListener('mouseleave', leave);
        el.removeEventListener('touchstart', enter);
        el.removeEventListener('touchend', leave);
      });
      chart.detach();
    };
  }, [points, unit, formatNumber]);

  return (
    <div
      ref={wrapperRef}
      data-testid="metric-chart"
      role="img"
      aria-label={ariaLabel}
      style={{ width: '100%', height: 200, position: 'relative' }}
    >
      <div ref={chartRef} style={{ width: '100%', height: '100%' }} />
      <div
        ref={tooltipRef}
        className="metric-chart-tooltip"
        role="tooltip"
        style={{
          position: 'absolute',
          transform: 'translate(-50%, calc(-100% - 8px))',
          padding: '4px 8px',
          background: 'rgba(0, 0, 0, 0.8)',
          color: '#fff',
          fontSize: 12,
          borderRadius: 4,
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          opacity: 0,
          transition: 'opacity 100ms',
          zIndex: 10,
        }}
      />
    </div>
  );
};

export default MetricChart;
